import moment from 'moment';

/**
 * Agrupa ventas/compras holding por mes calendario (YYYY-MM) sumando empresas.
 */
export function CombineMonthlySales(source) {
  const combinedSales = {};
  const list = Array.isArray(source) ? source : [];

  list.forEach(item => {
    const monthKey = item?.month
      ? moment(item.month).format('YYYY-MM')
      : String(item?.label || 'unknown');

    if (!combinedSales[monthKey]) {
      combinedSales[monthKey] = {
        ...item,
        sales: 0
      };
    }

    combinedSales[monthKey].sales += Number(item.sales) || 0;
  });

  return Object.values(combinedSales).sort((a, b) =>
    new Date(a.month) - new Date(b.month)
  );
};

/**
 * Garantiza 12 meses (índice 0=ene … 11=dic) para que GetDataset/GetLabels
 * puedan filtrar semestre/trimestre con slice por índice.
 */
export function ensureTwelveMonths(source, referenceYear) {
  const year = referenceYear ?? moment().year();
  const byMonthIndex = {};

  (Array.isArray(source) ? source : []).forEach((item) => {
    const parsed = moment(item?.month);
    if (!parsed.isValid()) return;
    const monthIndex = parsed.month();
    if (!byMonthIndex[monthIndex]) {
      byMonthIndex[monthIndex] = {
        ...item,
        sales: 0,
        month: moment({ year, month: monthIndex, day: 1 }).toDate(),
        label: moment({ year, month: monthIndex }).format('MMM-YYYY').replace(/\./g, ''),
      };
    }
    byMonthIndex[monthIndex].sales += Number(item.sales) || 0;
  });

  return Array.from({ length: 12 }, (_, monthIndex) => {
    if (byMonthIndex[monthIndex]) return byMonthIndex[monthIndex];
    return {
      sales: 0,
      month: moment({ year, month: monthIndex, day: 1 }).toDate(),
      label: moment({ year, month: monthIndex }).format('MMM-YYYY').replace(/\./g, ''),
    };
  });
}

export function CompanyMostAnnualSales(data) {
  const salesForCompany = {};

  data.forEach(item => {
    const companyId = item.companyId;
    const sales = item.sales || 0;

    if (salesForCompany[companyId]) {
      salesForCompany[companyId] += sales;
    } else {
      salesForCompany[companyId] = sales;
    }
  });

  let companyMostAnnualSales = null;
  let maxSales = -Infinity;

  for (const companyId in salesForCompany) {
    if (salesForCompany[companyId] > maxSales) {
      maxSales = salesForCompany[companyId];
      companyMostAnnualSales = companyId;
    }
  }
  return companyMostAnnualSales;
}

export function CompanyLessAnnualSales(data) {
  const salesForCompany = {};

  data.forEach(item => {
    const companyId = item.companyId;
    const sales = item.sales || 0;

    if (salesForCompany[companyId]) {
      salesForCompany[companyId] += sales;
    } else {
      salesForCompany[companyId] = sales;
    }
  });

  let companyLessAnnualSales = null;
  let minSales = Infinity;

  for (const companyId in salesForCompany) {
    if (salesForCompany[companyId] < minSales) {
      minSales = salesForCompany[companyId];
      companyLessAnnualSales = companyId;
    }
  }

  return {
    companyId: companyLessAnnualSales,
    totalVentas: minSales
  };
}

export function MonthMostSales(data) {
  if (!data || data.length === 0) {
    return null;
  }

  let maxSales = -Infinity;
  let monthMostSales = null;

  for (const item of data) {
    if (item.sales > maxSales) {
      maxSales = item.sales;
      monthMostSales = item;
    }
  }

  return monthMostSales;
}

export function MonthLowestSales(data) {
  if (!data || data.length === 0) {
    return null;
  }

  let minSales = Infinity;
  let monthWithLowestSales = null;

  for (const monthData of data) {
    if (monthData.sales == null || monthData.sales <= 0) continue;

    if (monthData.sales < minSales) {
      minSales = monthData.sales;
      monthWithLowestSales = monthData;
    }
  }

  return monthWithLowestSales;
}

export function sumDebtsForCompanies(data) {
  const resultado = data.reduce((acumulador, actual) => {
    const { companyId, valid, expired30, expired60, expired90, expiredOlder } = actual;
    
    if (!acumulador[companyId]) {
      acumulador[companyId] = {
        companyId,
        valid: 0,
        expired30: 0,
        expired60: 0,
        expired90: 0,
        expiredOlder: 0,
        registros: 0
      };
    }
    
    acumulador[companyId].valid += valid;
    acumulador[companyId].expired30 += expired30;
    acumulador[companyId].expired60 += expired60;
    acumulador[companyId].expired90 += expired90;
    acumulador[companyId].expiredOlder += expiredOlder;
    acumulador[companyId].registros += 1;
    acumulador[companyId].totalDebt = 
      acumulador[companyId].expired30 + 
      acumulador[companyId].expired60 + 
      acumulador[companyId].expired90 + 
      acumulador[companyId].expiredOlder;
    
    return acumulador;
  }, {});
  
  return Object.values(resultado);
}

export function groupByCompanyId(source) {
  const tempGrouped = {};
  
  source.forEach(item => {
    const companyId = item.companyId;
    
    if (!tempGrouped[companyId]) {
      tempGrouped[companyId] = [];
    }
    
    tempGrouped[companyId].push(item);
  });
  
  const result = [];
  
  for (const companyId in tempGrouped) {
    result.push({
      companyId: companyId,
      items: tempGrouped[companyId]
    });
  }
  
  return result;
}

export function findHighestAndLowestAmount(source) {
  let mayorMonto = -Infinity;
  let menorMonto = Infinity;
  let nombreMayor = '';
  let nombreMenor = '';
  
  for (const item of source) {
      const total = item.valid + item.expired30 + item.expired60 + item.expired90 + item.expiredOlder;
      
      if (total > mayorMonto) {
          mayorMonto = total;
          nombreMayor = item.name;
      }
      
      if (total < menorMonto && total > 0) {
          menorMonto = total;
          nombreMenor = item.name;
      }
  }
  
  return {
      mayorDeudor: {
          nombre: nombreMayor,
          monto: mayorMonto
      },
      menorDeudor: {
          nombre: nombreMenor,
          monto: menorMonto
      }
  };
}

export function processBankAccounts(accounts) {
  const result = {};
  
  accounts.forEach(account => {
    const currency = account.currencyType;
    
    if (!result[currency]) {
      result[currency] = {
        currencyType: currency,
        totalAvailableBalance: 0,
        totalCreditLineAvailable: 0,
        accountCount: 0,
        accounts: [] 
      };
    }
    
    result[currency].totalAvailableBalance += account.availableBalance;
    result[currency].totalCreditLineAvailable += account.creditLineAvailable;
    result[currency].accountCount++;
    result[currency].accounts.push(account);
  });
  
  return Object.values(result);
}

export function groupAccountsByCompanyId(source) {
  const grouped = source.reduce((acc, item) => {
    const { companyId } = item;
    
    if (!acc[companyId]) {
      acc[companyId] = [];
    }
    
    acc[companyId].push(item);
    return acc;
  }, {});
  
  return Object.entries(grouped).map(([companyId, items]) => ({
    companyId,
    items
  }));
}

